import { MXBaseComponent, convertToPlacement, AXFocusableComponent } from '@acorex/components/common';
import { AXPlatform, isBrowser } from '@acorex/core/platform';
import { Overlay } from '@angular/cdk/overlay';
import { TemplatePortal, ComponentPortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { inject, EventEmitter, ElementRef, TemplateRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ViewChild, Output, NgModule } from '@angular/core';
import { fromEvent, delay } from 'rxjs';
import { CommonModule } from '@angular/common';

class AXPopoverComponent extends MXBaseComponent {
    constructor() {
        super(...arguments);
        this._platform = inject(AXPlatform);
        this._overlay = inject(Overlay);
        this._isMouseOverButton = false;
        this._isMouseOverTooltip = false;
        this._targetEvents = {};
        this._overlayEvents = {};
        //
        this._offsetX = 0;
        //
        this._offsetY = 0;
        //
        this._defautPlacements = convertToPlacement('bottom-start');
        this._placements = [...this._defautPlacements];
        this._placement = this._placements;
        //
        this._openOn = 'toggle';
        //
        this._closeOn = 'clickOut';
        //
        this.hasBackdrop = false;
        //
        this.openAfter = 200;
        this.closeAfter = 200;
        this.adaptivityEnabled = false;
        //
        this.onOpened = new EventEmitter();
        this.onClosed = new EventEmitter();
    }
    get offsetX() {
        return this._offsetX;
    }
    set offsetX(v) {
        this.setOption({
            name: 'offsetX',
            value: v,
            afterCallback: () => {
                this.updateOffset();
            },
        });
    }
    get offsetY() {
        return this._offsetY;
    }
    set offsetY(v) {
        this.setOption({
            name: 'offsetY',
            value: v,
            afterCallback: () => {
                this.updateOffset();
            },
        });
    }
    get target() {
        return this._target;
    }
    set target(v) {
        this.removeTargetEvents();
        if (v instanceof HTMLElement)
            this._target = v;
        else if (v instanceof ElementRef)
            this._target = v.nativeElement;
        else if (typeof v?.getHostElement == 'function')
            this._target = v.getHostElement();
        //
        this.bindTargetEvents();
    }
    get placement() {
        return this._placement;
    }
    set placement(v) {
        this.setOption({
            name: 'placement',
            value: v,
            afterCallback: (o, n) => {
                const converted = convertToPlacement(n);
                this._placements = converted.length ? converted : this._defautPlacements;
                this.updateOffset();
                this.updatePositionStrategy();
            },
        });
    }
    get componentRef() {
        return this._componentRef;
    }
    get openOn() {
        return this._openOn;
    }
    set openOn(v) {
        this.setOption({
            name: 'openOn',
            value: v,
            afterCallback: () => {
                this.bindTargetEvents();
            },
        });
    }
    get closeOn() {
        return this._closeOn;
    }
    set closeOn(v) {
        this.setOption({
            name: 'closeOn',
            value: v,
            afterCallback: () => {
                this.bindTargetEvents();
            },
        });
    }
    _emitOnOpenedEvent() {
        this.onOpened.emit({
            component: this,
            htmlElement: this.getHostElement(),
        });
    }
    _emitOnClosedEvent() {
        this.onClosed.emit({
            component: this,
            htmlElement: this.getHostElement(),
        });
    }
    //
    _handleMouseEnter(e) {
        this._isMouseOverTooltip = true;
    }
    _handleMouseLeave(e) {
        this._isMouseOverTooltip = false;
        setTimeout(() => {
            if (!this._isMouseOverButton && this.closeOn == 'leave') {
                this.close();
            }
        }, 250);
    }
    removeTargetEvents() {
        Object.entries(this._targetEvents).forEach((e) => {
            e[1].unsubscribe();
        });
    }
    bindTargetEvents() {
        this.removeTargetEvents();
        if (!this._target)
            return;
        const targetMouseEnter$ = fromEvent(this._target, 'mouseenter');
        const targetMouseLeave$ = fromEvent(this._target, 'mouseleave');
        if (this.openOn == 'hover') {
            this._targetEvents.mouseenter = targetMouseEnter$.pipe(delay(this.openAfter)).subscribe(() => {
                this._isMouseOverButton = true;
                this.open();
            });
        }
        if (this.closeOn == 'leave') {
            this._targetEvents.mouseleave = targetMouseLeave$.pipe(delay(this.closeAfter)).subscribe(() => {
                this._isMouseOverButton = false;
                if (!this._isMouseOverTooltip) {
                    this.close();
                }
            });
        }
        if (this.openOn === 'click' || this.openOn === 'toggle') {
            const click$ = fromEvent(this._target, 'click');
            this._targetEvents.click = click$.subscribe((e) => {
                this.openOn == 'toggle' ? this.toggle() : this.open();
            });
        }
    }
    //
    bindOverlayEvents() {
        this.removeOverlayEvents();
        this._overlayEvents.attach = this._overlayRef.attachments().subscribe(() => {
            if (this.openOn == 'hover') {
                this._overlayRef.overlayElement.addEventListener('mouseenter', this._handleMouseEnter.bind(this));
            }
            if (this.closeOn == 'leave') {
                this._overlayRef.overlayElement.addEventListener('mouseleave', this._handleMouseLeave.bind(this));
            }
            //
            this._overlayEvents.outside = this._overlayRef._outsidePointerEvents.subscribe((e) => {
                const el = e.target;
                if (this.closeOn == 'clickOut' && this.isOpen && !this._target.contains(el) && !this._overlayRef?.overlayElement?.contains(el)) {
                    this.close();
                }
            });
            //
            this._overlayEvents.scroll = this._platform.scroll.subscribe((c) => {
                //__axContext__
                const el = c.nativeEvent.target;
                if (!this.isActionsheetStyle) {
                    if (el == document) {
                        this.close();
                        return;
                    }
                    if (el?.closest &&
                        !el.closest('.ax-overlay-pane') &&
                        !this._target.contains(el) &&
                        !this._overlayRef?.overlayElement?.contains(el)) {
                        this.close();
                    }
                }
            });
        });
    }
    removeOverlayEvents() {
        this._overlayRef?.overlayElement.removeEventListener('mouseenter', this._handleMouseEnter.bind(this));
        this._overlayRef?.overlayElement.addEventListener('mouseleave', this._handleMouseLeave.bind(this));
        Object.entries(this._overlayEvents).forEach((e) => {
            e[1].unsubscribe();
        });
    }
    //
    toggle() {
        this.isOpen ? this.close() : this.open();
    }
    //
    close() {
        if (!this.isOpen) {
            return;
        }
        this._overlayRef?.detach();
        this.restoreFocus();
        this._emitOnClosedEvent();
    }
    //
    open() {
        if (this.isOpen) {
            return;
        }
        this.saveFocus();
        this.openOverlayInternal();
        this._emitOnOpenedEvent();
    }
    saveFocus() {
        if (isBrowser()) {
            this._lastActiveElement = document.activeElement;
        }
    }
    restoreFocus() {
        if (this._lastActiveElement?.focus) {
            this._lastActiveElement.focus();
        }
    }
    openOverlayInternal() {
        const targetRef = this._target;
        if (!targetRef)
            return;
        //if (!this._overlayRef) {
        if (this.isActionsheetStyle) {
            this._overlayRef = this._overlay.create({
                positionStrategy: this._overlay.position().global().bottom().centerHorizontally(),
                disposeOnNavigation: true,
                scrollStrategy: this._overlay.scrollStrategies.block(),
                panelClass: ['ax-actionsheet-base', 'ax-animate-slideInUp', 'ax-animate-faster'],
                hasBackdrop: true,
                width: '100%',
            });
        }
        else {
            this._overlayRef = this._overlay.create({
                positionStrategy: this._overlay.position().flexibleConnectedTo(targetRef).withPositions(this._placements).withPush(false),
                scrollStrategy: this._overlay.scrollStrategies.noop(),
                disposeOnNavigation: true,
                panelClass: ['ax-animate-fadeIn', 'ax-animate-faster'],
                maxHeight: 'unset',
                hasBackdrop: this.hasBackdrop,
                backdropClass: [this.backdropClass || 'cdk-overlay-transparent-backdrop'],
            });
        }
        //
        this.bindOverlayEvents();
        //}
        //
        if (this.content instanceof TemplateRef) {
            this._portal = new TemplatePortal(this.content, this.getViewContainer(), {
                $implicit: this.context,
                ref: this,
            });
            this._overlayRef?.attach(this._portal);
        }
        else if (typeof this.content === 'function') {
            this._portal = new ComponentPortal(this.content);
            this._componentRef = this._overlayRef?.attach(this._portal);
            Object.assign(this._componentRef.instance, this.context);
        }
        else {
            this._portal = new TemplatePortal(this._baseTemplate, this.getViewContainer(), {
                $implicit: this.context,
                ref: this,
            });
            this._overlayRef?.attach(this._portal);
        }
    }
    get isOpen() {
        return this._overlayRef ? this._overlayRef.hasAttached() : false;
    }
    get isActionsheetStyle() {
        return this._platform.is('SM') && this.adaptivityEnabled;
    }
    updatePositionStrategy() {
        const targetRef = this._target;
        if (!targetRef)
            return;
        let strategy;
        if (this.isActionsheetStyle) {
            strategy = this._overlay.position().global().bottom().centerHorizontally();
        }
        else {
            strategy = this._overlay.position().flexibleConnectedTo(targetRef).withPositions(this._placements).withPush(false);
        }
        this._overlayRef?.updatePositionStrategy(strategy);
    }
    updateOffset() {
        this._placements?.forEach((p) => {
            if (this.offsetY != null)
                p.offsetY = this.offsetY;
            if (this.offsetX != null)
                p.offsetX = this.offsetX;
        });
    }
    updatePosition() {
        this._overlayRef?.updatePosition();
        this.focus();
    }
    focus() {
        //TODO: need this??
    }
    //
    ngOnDestroy() {
        this.removeTargetEvents();
        this.removeOverlayEvents();
        this._overlayRef?.detach();
        this._overlayRef?.dispose();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.2", type: AXPopoverComponent, selector: "ax-popover", inputs: { offsetX: "offsetX", offsetY: "offsetY", target: "target", placement: "placement", content: "content", openOn: "openOn", closeOn: "closeOn", hasBackdrop: "hasBackdrop", openAfter: "openAfter", closeAfter: "closeAfter", backdropClass: "backdropClass", adaptivityEnabled: "adaptivityEnabled" }, outputs: { onOpened: "onOpened", onClosed: "onClosed" }, providers: [{ provide: AXFocusableComponent, useExisting: AXPopoverComponent }], viewQueries: [{ propertyName: "_baseTemplate", first: true, predicate: ["baseTemplate"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<ng-template #baseTemplate>\n  <ng-content></ng-content>\n</ng-template>", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-popover', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, providers: [{ provide: AXFocusableComponent, useExisting: AXPopoverComponent }], template: "<ng-template #baseTemplate>\n  <ng-content></ng-content>\n</ng-template>" }]
        }], propDecorators: { offsetX: [{
                type: Input
            }], offsetY: [{
                type: Input
            }], target: [{
                type: Input
            }], placement: [{
                type: Input
            }], _baseTemplate: [{
                type: ViewChild,
                args: ['baseTemplate', { static: true }]
            }], content: [{
                type: Input
            }], openOn: [{
                type: Input
            }], closeOn: [{
                type: Input
            }], hasBackdrop: [{
                type: Input
            }], openAfter: [{
                type: Input
            }], closeAfter: [{
                type: Input
            }], backdropClass: [{
                type: Input
            }], adaptivityEnabled: [{
                type: Input
            }], onOpened: [{
                type: Output
            }], onClosed: [{
                type: Output
            }] } });

class AXPopoverModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverModule, declarations: [AXPopoverComponent], imports: [CommonModule], exports: [AXPopoverComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AXPopoverComponent],
                    imports: [CommonModule],
                    exports: [AXPopoverComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXPopoverComponent, AXPopoverModule };
